import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import phone from "phone";
import { DataContext } from "../dataContext";
import { useLocation } from "react-router-dom";
import useApi from "../api/api";
import { ReactComponent as PayPalIcon } from "../assets/ico_paypal.svg";
import { ReactComponent as VenmoIcon } from "../assets/ico_venmo.svg";
import { ReactComponent as CardIcon } from "../assets/ico_card.svg";
import { ReactComponent as BankIcon } from "../assets/ico_bank.svg";
import { ReactComponent as GiftCardIcon } from "../assets/ico_gift_card.svg";
import { ReactComponent as CoinbaseIcon } from "../assets/ico_coinbase.svg";
import { ReactComponent as GoogleIcon } from "../assets/ico_google.svg";
import { ReactComponent as AppleIcon } from "../assets/ico_apple.svg";
// Cash App's own symbol, byte-for-byte from design.block.xyz/cash-app/guidelines/logo
// (cash_symbol.zip). Their guidelines prohibit altering it, so nothing is applied on top —
// no recolouring, no CSS rounding. Cash Green is #00E013; the "$" is a knockout, so the
// button background shows through it. On our white buttons that is their sanctioned
// green-on-white variant.
import { ReactComponent as CashAppSymbol } from "../assets/cash_symbol_green.svg";
import { ReactComponent as CheckExample } from "../assets/check_example.svg";

// Lucide-style line glyphs drawn inline so they take `currentColor`.
const glyph = {
  bookmark: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 3.5h11a1 1 0 0 1 1 1v16l-6.5-4-6.5 4v-16a1 1 0 0 1 1-1Z" />
    </svg>
  ),
  message: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5.5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-9l-5 4v-4H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z" />
    </svg>
  ),
  chevron: (
    <svg
      className="np-chevron"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  ),
  chevronLeft: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 6 9 12 15 18" />
    </svg>
  ),
  check: (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="4 13 9 18 20 6" />
    </svg>
  ),
  clock: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="8" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  ),
  cross: (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  ),
  search: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.5" y2="16.5" />
    </svg>
  ),
};

export const ExternalTransfer = ({ branding }) => {
  const api = useApi();
  const { transferData, setTransferData } = useContext(DataContext);

  const location = useLocation();

  const transferId = location.search.split("?t=")[1].split("&i=")[0];
  const inviteId = location.search.split("&i=")[1];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorIdentifier, setErrorIdentifier] = useState("");
  const [errorBankAccount, setErrorBankAccount] = useState(false);
  const [errorBankRouting, setErrorBankRouting] = useState(false);
  const [errorFirstName, setErrorFirstName] = useState(false);
  const [errorLastName, setErrorLastName] = useState(false);
  const [paymentType, setPaymentType] = useState("");
  const [coinbaseError, setCoinbaseError] = useState("");
  // Google Pay payout: the row only shows once this browser says it can open the sheet.
  const [visaAliasDirectory, setVisaAliasDirectory] = useState("");
  // Visa Alias recipient verification (NPSD-1939). The recipient supplies their own name plus a
  // phone number whose last 4 we send as the wallet's contact number; the backend checks both
  // against the wallet's record and answers with a verdict only. The wallet's name-on-file is
  // deliberately never returned here — echoing it would let anyone holding a phone number learn
  // the name on that wallet account.
  //
  // `phone` is only collected when the alias itself isn't a phone number (email or cashtag).
  // When the alias IS a phone number we take the last 4 from it — see `visaAliasLast4`.
  const [visaAliasDetails, setVisaAliasDetails] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [visaAliasError, setVisaAliasError] = useState("");
  // Attempts left after the last verification call. The wallet-action screen is its own view
  // rather than an error string, so it needs the count separately — that press burned an attempt
  // like any other, and sending someone off to their wallet without saying so is how they come
  // back to a locked-out payment.
  const [visaAliasAttemptsLeft, setVisaAliasAttemptsLeft] = useState(null);
  // Latched once the Visa Sensory Branding animation has run for this transfer, so a repeat
  // "Succeeded" poll doesn't replay it and trap the recipient in a loop. A ref rather than
  // state: it must be readable from the polling effect without joining its dependency list,
  // where a stale closure would re-enter the animation.
  const sensoryPlayedRef = useRef(false);
  const [errorVisaAliasFirstName, setErrorVisaAliasFirstName] = useState(false);
  const [errorVisaAliasLastName, setErrorVisaAliasLastName] = useState(false);
  const [errorVisaAliasPhone, setErrorVisaAliasPhone] = useState(false);
  const prevViewStateRef = useRef("");

  const [recipientType, setRecipientType] = useState("");
  const [recipientIdentifier, setRecipientIdentifier] = useState("");
  const [recipientAccountDetails, setRecipientAccountDetails] = useState({
    routing_number: "",
    account_number: "",
    firstName: "",
    lastName: "",
  });
  const [statusDate, setStatusDate] = useState(new Date());
  const [viewState, setViewState] = useState("");
  const [frameHeight, setFrameHeight] = useState("530px");
  // Payee Management POC: the recipient's saved default for this FI (one-tap screen), and the
  // "save this" / "text me" choices on the claim screens.
  const [savedMethod, setSavedMethod] = useState(null);
  const savedMethodCheckedRef = useRef(false);
  // Success-screen offers: { savable_label, sms_available } from the backend, and what was chosen.
  const [afterClaim, setAfterClaim] = useState(null);
  const [methodSaved, setMethodSaved] = useState(false);
  const [savedLabel, setSavedLabel] = useState("");
  const [smsOptedIn, setSmsOptedIn] = useState(false);
  const [afterClaimBusy, setAfterClaimBusy] = useState(false);
  // "Deposit to your <FI> card ending 1234": the recipient's own card we already know.
  const [knownAccount, setKnownAccount] = useState(null);
  const knownAccountCheckedRef = useRef(false);

  useEffect(() => {
    if (transferData.transfer_state === "Succeeded" && paymentType === "pushtoaccount") {
      //fill in the info from the transferData if the transfer is successful
      setRecipientAccountDetails({
        firstName: transferData.payeeFirstName,
        lastName: transferData.payeeLastName,
        routing_number: transferData.recipient_identifier.split("_")[0],
        account_number: transferData.recipient_identifier.split("_")[1],
      });
    }
    if (transferData.payeeEmail) {
      setRecipientIdentifier(transferData.payeeEmail);
      setRecipientType("email");
    } else {
      setRecipientIdentifier(transferData.recipient_identifier);
      setRecipientType(transferData.recipient_type);
    }
  }, [transferData]);

  useEffect(() => {
    prevViewStateRef.current = viewState;
  }, [viewState]);

  const handleRecipientIdentifierChange = ({ target: { value } }) => {
    setRecipientIdentifier(value ? value : "");
  };

  const handleVisaAliasDetailsChange = ({ target: { id, value } }) => {
    if (id === "visa_alias_phone") {
      // Accept whatever separators the user types; we only ever read the digits back out.
      setVisaAliasDetails({ ...visaAliasDetails, phone: value });
      return;
    }
    const field = id === "visa_alias_first_name" ? "firstName" : "lastName";
    setVisaAliasDetails({ ...visaAliasDetails, [field]: value });
  };

  /**
   * Which kind of alias the recipient typed. Visa accepts several alias types against a
   * directory; we support the three Cash App exposes.
   *
   * A cashtag is Visa's NATIVE_ALIAS type and always starts with "$", which is what makes it
   * unambiguous against a phone number or email.
   */
  const visaAliasKind = () => {
    const value = recipientIdentifier.trim();
    if (value.includes("@")) {
      return "email";
    }
    if (value.startsWith("$")) {
      return "cashtag";
    }
    return "phone";
  };

  // Whether we need to ask for a phone number separately. Only when the alias itself isn't one.
  const visaAliasNeedsPhone = () => visaAliasKind() !== "phone";

  const digitsOf = (value) => (value || "").replace(/\D/g, "");

  /**
   * The `contactNumberLast4` we send to Visa.
   *
   * For a phone alias we take it from the alias itself. For email and cashtag aliases the
   * recipient gives us a phone number and we take it from that.
   *
   * Note this is the wallet's *contact number*, which Visa treats as a separate field from the
   * alias — they are not guaranteed to be the same number. Deriving it from a phone alias is
   * what Visa asked for in Aug 2026; see the note on `contact_number_last4` in the backend.
   */
  const visaAliasLast4 = () => {
    const source = visaAliasNeedsPhone()
      ? visaAliasDetails.phone
      : recipientIdentifier;
    return digitsOf(source).slice(-4);
  };

  /**
   * Sanity-check the alias on the Visa Alias screen — deliberately NOT the strict NANP check
   * `confirmIdentifier()` applies elsewhere.
   *
   * The directory is the real authority on whether an alias exists, and NANP validation
   * rejects aliases that are perfectly valid to Visa: their own test numbers (3837258187,
   * 1231231234) use unassigned area codes, and any non-US number fails outright. Getting the
   * alias wrong is also cheap now — an unknown alias comes back "not found" and doesn't consume
   * a verification attempt.
   *
   * This also does not rewrite the value. `confirmIdentifier()` normalizes to +1 E.164, which
   * would silently change what we send to Visa; the alias goes to the directory as entered.
   */
  const confirmVisaAliasIdentifier = () => {
    const value = recipientIdentifier.trim();
    let ok;
    switch (visaAliasKind()) {
      case "email":
        ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.toLowerCase());
        break;
      case "cashtag":
        // "$" plus letters/digits/underscore. Kept loose on purpose, per the note above: the
        // directory is the authority on whether a cashtag exists, not us.
        ok = /^\$[A-Za-z0-9_]{1,30}$/.test(value);
        break;
      default:
        // E.164 allows 7–15 digits; accept any separators the user typed.
        ok =
          /^\+?[\d\s()\-.]+$/.test(value) &&
          digitsOf(value).length >= 7 &&
          digitsOf(value).length <= 15;
    }

    setErrorIdentifier(!ok);
    return ok;
  };

  // Validate the fields on the Visa Alias entry screen. Returns true when the form is
  // complete enough to send; sets the per-field error flags either way.
  const confirmVisaAliasDetails = () => {
    const first = visaAliasDetails.firstName.trim();
    const last = visaAliasDetails.lastName.trim();
    // The separate phone field only exists for email and cashtag aliases. When the alias is
    // itself a phone number there is nothing to validate here — `confirmVisaAliasIdentifier`
    // has already checked it has 7–15 digits, so a last 4 is always available.
    const phoneOk = visaAliasNeedsPhone()
      ? digitsOf(visaAliasDetails.phone).length >= 7 &&
        digitsOf(visaAliasDetails.phone).length <= 15
      : true;

    setErrorVisaAliasFirstName(!first);
    setErrorVisaAliasLastName(!last);
    setErrorVisaAliasPhone(!phoneOk);

    return Boolean(first) && Boolean(last) && phoneOk;
  };

  const handleRecipientAccountDetailsChange = ({ target: { id, value } }) => {
    switch (id) {
      case "routing_number":
        // US routing numbers are 9 digits — don't let the field exceed that.
        if (value.length > 9) {
          return;
        } else {
          setRecipientAccountDetails({
            ...recipientAccountDetails,
            [id]: value,
          });
        }

        break;
      case "account_number":
        if (value.length > 17) {
          return;
        } else {
          setRecipientAccountDetails({
            ...recipientAccountDetails,
            [id]: value,
          });
        }

        break;
      case "firstName":
        setRecipientAccountDetails({
          ...recipientAccountDetails,
          firstName: value,
        });
        break;
      case "lastName":
        setRecipientAccountDetails({
          ...recipientAccountDetails,
          lastName: value,
        });
        break;
      default: {
        setRecipientAccountDetails({
          ...recipientAccountDetails,
          [id]: value,
        });
      }
    }
  };

  const returnHome = () => {
    setErrorIdentifier(false);
    setLoading(false);
    setViewState("default");
  };

  const guessRecipientType = (test) => {
    if (paymentType === "pushtoaccount") {
      return "routing_account";
    } else {
      return test.includes("@") ? "email" : "phone";
    }
  };

  const confirmIdentifier = () => {
    // Wallet type is already validated and set in the Coinbase wallet flow
    if (recipientType === "walletaddress") {
      return true;
    }
    const identifierType = guessRecipientType(recipientIdentifier);
    setRecipientType(identifierType);

    if ("venmo" === paymentType && "email" === identifierType) {
      setErrorIdentifier(true);
      setLoading(false);
      return false;
    }

    if ("phone" === identifierType) {
      const parsed = phone(recipientIdentifier, { country: "USA" });
      if (!parsed.isValid) {
        setErrorIdentifier(true);
        setLoading(false);
        return false;
      }
      setRecipientIdentifier(parsed.phoneNumber);
    }

    if ("email" === identifierType) {
      const e =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!e.test(recipientIdentifier.toLowerCase())) {
        setErrorIdentifier(true);
        setLoading(false);
        return false;
      }
    }

    setErrorIdentifier(false);
    setLoading(false);
    return true;
  };

  /**
   * Validates bank account details by checking:
   * - Routing number is at least 9 characters
   * - Account number is at least 4 characters
   * - First name is not empty
   * - Last name is not empty
   * 
   * Routing and account numbers must be digits only — people paste them
   * off a check with formatting like "0123-4567.8" or spaces, which breaks
   * the transfer downstream. We reject anything with a non-digit and point
   * them at the account number on a check (illustration on the form).
   */
  const confirmBankAccount = async () => {
    const routing = recipientAccountDetails.routing_number;
    const account = recipientAccountDetails.account_number;
    // /\D/ = any character that is not a digit (dash, dot, space, letter, ...).
    // US routing numbers (ABA) are always exactly 9 digits.
    const routingInvalid = routing.length !== 9 || /\D/.test(routing);
    const accountInvalid = account.length < 4 || /\D/.test(account);

    setErrorBankRouting(routingInvalid);
    setErrorBankAccount(accountInvalid);

    if (!recipientAccountDetails.firstName.trim()) {
      setErrorFirstName(true);
    } else {
      setErrorFirstName(false);
    }
    if (!recipientAccountDetails.lastName.trim()) {
      setErrorLastName(true);
    } else {
      setErrorLastName(false);
    }
    if (
      routingInvalid ||
      accountInvalid ||
      !recipientAccountDetails.firstName.trim() ||
      !recipientAccountDetails.lastName.trim()
    ) {
      return false;
    }

    return true;
  };


  /**
   * Checks if a bank routing number is supported by the P2A (Push to Account) provider
   * by making an API call to verify capabilities.
   * 
   * @param {string} routingNumber - The bank routing number to validate
   * @returns {Promise<void>} 
   * @throws Will throw an error if the API call fails
   */
  const checkP2AProviderCapabilities = async (routingNumber) => {
    const postData = {
      routing_number: routingNumber,
    };
    
    setLoading(true);
    try {
      const response = await api.post(
        `/external/transfer/${transferId}/${inviteId}/capabilities`,
        postData
      );

      // Based on P2A capabilities, show the appropriate screen to the user.
      setStatusDate(new Date());
      switch (true) {
        case !response.data.success:
          setViewState("confirmP2A_no_service");
          break;
        case !response.data.fi_capabilities.can_receive:
          setViewState("confirmP2A_no_receive");
          break;
        case !response.data.fi_capabilities.instant_transfer:
          setViewState("confirmP2A_no_instant_transfer");
          break;
        default:
          setViewState("confirmP2A");
          break;
      }
    } catch (err) {
      console.error('Capabilities error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const getRecipientIdentifierDisplay = () => {
    // A cashtag has no digits to slice into a phone shape — show it as typed. Gated on the
    // payment type as well as the "$", so this cannot affect another rail even if some future
    // identifier format happens to start with one.
    if (paymentType === "visaalias" && recipientIdentifier.trim().startsWith("$")) {
      return <b>{recipientIdentifier.trim()}</b>;
    }
    switch (recipientIdentifier.includes("@")) {
      case false: {
        return (
          <b>
            ({recipientIdentifier.slice(-10, -7)}){" "}
            {recipientIdentifier.slice(-7, -4)}-{recipientIdentifier.slice(-4)}
          </b>
        );
      }
      default:
        return <b>{recipientIdentifier}</b>;
    }
  };

  const getPaymentTypeDisplay = () => {
    switch (paymentType) {
      case "paypal": {
        return <b>PayPal</b>;
      }
      case "venmo": {
        return <b>Venmo</b>;
      }
      case "pushtocard": {
        return <b>Direct to Debit Card</b>;
      }
      case "pushtoaccount": {
        return <b>Direct to Account</b>;
      }
      case "prizeoutcard": {
        return <b>Prizeout</b>;
      }
      case "coinbase": {
        return <b>Coinbase</b>;
      }
      case "googlepay": {
        return <b>Google Pay</b>;
      }
      case "applepay": {
        return <b>Apple Pay</b>;
      }
      case "visaalias": {
        return <b>{visaAliasMeta(visaAliasDirectory).name}</b>;
      }
      default: {
        return "";
      }
    }
  };

  const postAfterClaim = async (body) => {
    try {
      const response = await api.post(
        `/external/transfer/${transferId}/${inviteId}/after-claim`,
        body
      );
      return response.data;
    } catch (err) {
      console.warn("could not save after-claim choice", err);
      return null;
    }
  };

  const acceptSavedMethod = async () => {
    setLoading(true);
    try {
      await api.post(`/external/transfer/${transferId}/${inviteId}/saved-method/accept`);
      setPaymentType(savedMethod.payment_type.split(":")[0]);
      setStatusDate(new Date());
      setViewState("processing");
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const submitTransfer = async () => {
    setLoading(true);
    setErrorIdentifier(false);

    // Visa Alias: the alias and the recipient's identity were already verified server-side
    // by the resolve step, and the backend re-checks that persisted verdict before it will
    // pay out — so skip the local phone/email heuristics here rather than block the submit
    // with no visible error.
    const isVisaAlias = paymentType === "visaalias";
    if (isVisaAlias || confirmIdentifier()) {
      const postData = {
        payment_type: paymentType,
        // Visa Alias: must be the same classification the resolve step sent, since the backend
        // maps it to the Visa aliasType again on the payout path and binds the stored verdict to
        // the alias. Getting it wrong here verifies the recipient and then bounces the payment.
        recipient_type: isVisaAlias ? visaAliasKind() : recipientType,
        recipient_identifier: isVisaAlias
          ? recipientIdentifier.trim()
          : recipientIdentifier,
      };

      // Add first and last name for bank account transfers
      if (paymentType === "pushtoaccount") {
        postData.first_name = recipientAccountDetails.firstName;
        postData.last_name = recipientAccountDetails.lastName;
      }

      // Visa Alias: send the name the recipient verified with, so it lands on the transfer
      // record instead of being dropped.
      if (isVisaAlias) {
        postData.first_name = visaAliasDetails.firstName.trim();
        postData.last_name = visaAliasDetails.lastName.trim();
      }

      // Visa Alias: carry the chosen directory (wallet) as a payment_type suffix so the
      // backend pins the resolve to that directory, e.g. "visaalias:cashapp".
      if (paymentType === "visaalias" && visaAliasDirectory) {
        postData.payment_type = `visaalias:${visaAliasDirectory}`;
      }

      try {
        await api.post(
          `/external/transfer/${transferId}/${inviteId}`,
          postData
        );
        setStatusDate(new Date());
        setViewState("processing");
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteTransfer = async () => {
    setLoading(true);
    try {
      await api.delete(`/external/transfer/${transferId}/${inviteId}`);
      setStatusDate(new Date());
      setViewState("declined");
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const backOutDeleteTransfer = async () => {
    setLoading(false);
    setViewState("default");
  };

  const pollForTransferStatus = useCallback(async () => {
    try {
      const response = await api.get(
        `/external/transfer/${transferId}/${inviteId}`
      );
      if (
        !(
          (response.data.transfer_state === "WaitForExternalInput" && viewState !== "processing") ||
          response.data.paymentStatus === "Pending"
        )
      ) {
        setTransferData(response.data);
      }
      // TODO disbursements date
      transferData.transfer_state_date &&
        setStatusDate(new Date(transferData.transfer_state_date));
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [api, inviteId, transferId, transferData, setTransferData, viewState]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        (viewState === "changeP2CCard" ||
          viewState === "googlePayFrame" ||
          viewState === "processing") &&
        transferData &&
        // TODO disbursements status
        transferData.transfer_state !== "Succeeded" &&
        transferData.transfer_state !== "Failed"
      ) {
        pollForTransferStatus();
      } else if (
        transferData.transfer_state === "Succeeded" ||
        transferData.transfer_state === "Failed"
      ) {
        setFrameHeight("200px");
      }
    }, 3000);
    setTimeout(() => {
      clearInterval(interval);
    }, 600000);
    return () => clearInterval(interval);
  }, [transferData, viewState, pollForTransferStatus]);

  const isRequestForPay = transferData?.originated_as_rfp === true;

  /**
   * Visa Sensory Branding: the animated Visa mark that plays between processing and the
   * confirmation screen on Visa rails. Mandatory for new implementations per Visa's brand
   * standards, and explicitly requested for this flow in their Aug 2026 UX review.
   *
   * The SDK is a global script that injects its own iframe, so it is loaded on demand rather
   * than in index.html — no reason to pull ~460KB for a PayPal or push-to-card payout. It
   * signals completion by postMessage, which is why the listener is on `window` and why it
   * checks the payload exactly: this app is itself embedded in an iframe, so unrelated
   * cross-frame traffic reaches this handler too.
   *
   * Everything here fails open. A missing script, a blocked asset, or an end event that never
   * arrives must not strand a recipient on a spinner after their money has already moved, so
   * both the error path and a watchdog advance to the confirmation screen regardless.
   */
  useEffect(() => {
    if (viewState !== "visaSensory") {
      return undefined;
    }

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      sensoryPlayedRef.current = true;
      setViewState("successful");
    };

    const onMessage = (e) => {
      if (e.data === "visa-sensory-branding-end") {
        finish();
      }
    };
    window.addEventListener("message", onMessage);

    // Backdrop follows the FI's own branding; the SDK picks a blue or white mark based on
    // contrast, and warns in the console if the ratio drops under 3:1.
    const backdrop = (
      branding?.receiving_colors?.receiving_bg_primary || "#FFFFFF"
    ).replace("#", "");

    const play = () => {
      try {
        window.VisaSensoryBranding.init(
          {
            // Full screen, not constrained: Visa's guide reserves constrained for layouts with
            // "many other elements", and specifies full screen on small devices.
            constrained: false,
            checkmark: "checkmarkWithText",
            checkmarkTextOption: "success",
            color: backdrop,
            language: "en",
            // Visa asks for animation, sound and haptic together "whenever possible". Mobile
            // browsers block the audio under autoplay policy, so in practice this only fires
            // on desktop.
            sound: true,
          },
          "/VisaSensoryBrandingSDK"
        );
        window.VisaSensoryBranding.show();
      } catch (err) {
        console.error("Visa Sensory Branding failed to start:", err);
        finish();
      }
    };

    if (window.VisaSensoryBranding) {
      play();
    } else {
      const script = document.createElement("script");
      script.src = "/VisaSensoryBrandingSDK/visa-sensory-branding.js";
      script.async = true;
      script.onload = play;
      script.onerror = () => {
        console.error("Visa Sensory Branding SDK failed to load");
        finish();
      };
      document.body.appendChild(script);
    }

    // Watchdog. The animation runs a few seconds; if its end event is lost we move on anyway.
    const watchdog = setTimeout(finish, 8000);

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(watchdog);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState]);

  useEffect(() => {
    // TODO disbursements type
    // Nothing fetched yet (DataProvider starts with {}): leave viewState unset so
    // no screen renders, rather than falling through to "processing" below.
    if (Object.keys(transferData).length === 0) return;
    transferData.payment_type && setPaymentType(transferData.payment_type);
    // Disbursements send an upper-case paymentStatus ("PENDING" -> "Pending").
    // Without one this must stay undefined, not `undefined + undefined` (NaN).
    const paymentStatus = transferData.paymentStatus
      ? transferData.paymentStatus.charAt(0) +
        transferData.paymentStatus.slice(1).toLowerCase()
      : undefined;
    switch (transferData.transfer_state || paymentStatus) {
      case "Failed":
      case "Cancelled": {
        setViewState("failed");
        break;
      }
      case "Succeeded": {
        // Visa requires the Sensory Branding animation to play "after process and before
        // confirmation screen" for payments on Visa rails, so a successful Visa Alias transfer
        // detours through `visaSensory` on its way to `successful`. Read the payment type off
        // transferData rather than the `paymentType` state, which the setState above has not
        // flushed yet. Every other rail goes straight to `successful` as before.
        const onVisaRail = (transferData.payment_type || "").startsWith("visaalias");
        setViewState(
          onVisaRail && !sensoryPlayedRef.current ? "visaSensory" : "successful"
        );
        break;
      }
      case "InviteRequestExternal":
      case "WaitForRequesteeCardInput": {
        // Request for Pay — skip the payment-method chooser and go
        // straight to the CDE card-entry iframe. The requestee is
        // expected to pay via debit card only.
        setPaymentType("pushtocard");
        setViewState("changeP2CCard");
        break;
      }
      case "WaitForExternalInput": {
        if (prevViewStateRef.current === "processing" && paymentType === "coinbase") {
          setCoinbaseError("This payout method is currently unavailable. Please choose another option.");
        }
        setViewState("default");
        break;
      }
      case "Pending": {
        setViewState("default");
        break;
      }
      case "server_down": {
        setViewState("server_down");
        break;
      }
      case undefined: {
        setViewState("not_found");
        break;
      }
      default: {
        setViewState("processing");
        break;
      }
    }
  }, [transferData]);

  useEffect(() => {
    if (viewState !== "default" || isRequestForPay || savedMethodCheckedRef.current) {
      return;
    }
    savedMethodCheckedRef.current = true;
    const lookup = async () => {
      try {
        const response = await api.get(
          `/external/transfer/${transferId}/${inviteId}/saved-method`
        );
        setSavedMethod(response.data);
        setViewState("oneTap");
      } catch (err) {
        // 404: no saved method for this FI — stay on the normal chooser.
      }
    };
    lookup();
  }, [viewState, isRequestForPay, api, transferId, inviteId]);

  // Once the payment has gone through, ask whether to remember how it was received.
  useEffect(() => {
    if (viewState !== "successful" || isRequestForPay || afterClaim) {
      return;
    }
    const load = async () => {
      try {
        const response = await api.get(
          `/external/transfer/${transferId}/${inviteId}/after-claim`
        );
        setAfterClaim(response.data);
        setMethodSaved(Boolean(response.data.method_saved));
        setSavedLabel(response.data.method_saved ? response.data.savable_label : "");
        setSmsOptedIn(Boolean(response.data.sms_opted_in));
      } catch (err) {
        setAfterClaim({ savable_label: null, sms_available: false });
      }
    };
    load();
  }, [viewState, isRequestForPay, afterClaim, api, transferId, inviteId]);

  // Both choices are plain on/off toggles: each change is sent straight to the backend, and the
  // box only flips once the backend has accepted it.
  const toggleSaveMethod = async (checked) => {
    setAfterClaimBusy(true);
    const result = await postAfterClaim(
      checked ? { save_method: true } : { save_method: false }
    );
    if (result) {
      setMethodSaved(checked);
      setSavedLabel(checked ? result.saved_label || "" : "");
    }
    setAfterClaimBusy(false);
  };

  const toggleSms = async (checked) => {
    setAfterClaimBusy(true);
    const result = await postAfterClaim({ sms_opt_in: checked });
    if (result) setSmsOptedIn(checked);
    setAfterClaimBusy(false);
  };

  // Phone recipients: saving and texts are one choice — a saved method is managed by texted
  // sign-in codes, which need SMS consent. Off until they turn it on.
  const toggleSaveAndSms = async (checked) => {
    setAfterClaimBusy(true);
    const result = await postAfterClaim({ save_and_sms: checked });
    if (result) {
      setMethodSaved(checked);
      setSmsOptedIn(checked);
      setSavedLabel(checked ? result.saved_label || "" : "");
    }
    setAfterClaimBusy(false);
  };

  const renderAfterClaimOptions = () => {
    if (!afterClaim || (!afterClaim.savable_label && !afterClaim.sms_available)) {
      return null;
    }
    const toggleRow = ({ id, icon, title, detail, checked, onChange }) => (
      <label className="np-toggle-row" htmlFor={id}>
        <span className="np-toggle-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="np-toggle-text">
          <span className="np-toggle-title">{title}</span>
          <span className="np-toggle-detail">{detail}</span>
        </span>
        <input
          id={id}
          type="checkbox"
          role="switch"
          className="np-switch-input"
          checked={checked}
          disabled={afterClaimBusy}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="np-switch" aria-hidden="true" />
      </label>
    );

    const combined = Boolean(afterClaim.save_and_sms_language);

    return (
      <div className="np-claim-options np-toggle-card">
        {combined &&
          toggleRow({
            id: "toggle-save-and-sms",
            icon: glyph.bookmark,
            title: methodSaved
              ? `Saved · texts on for ${afterClaim.sms_phone}`
              : `Save this and text me at ${afterClaim.sms_phone}`,
            // Rendered verbatim from the backend, which stores this exact text as consent evidence.
            detail: afterClaim.save_and_sms_language,
            checked: methodSaved,
            onChange: toggleSaveAndSms,
          })}
        {!combined &&
          afterClaim.savable_label &&
          toggleRow({
            id: "toggle-save-method",
            icon: glyph.bookmark,
            title: methodSaved ? "Saved for next time" : "Save for next time",
            detail:
              methodSaved && savedLabel
                ? `${savedLabel.charAt(0).toUpperCase()}${savedLabel.slice(1)}. We'll always ask before using it.`
                : "We'll always ask before using it.",
            checked: methodSaved,
            onChange: toggleSaveMethod,
          })}
        {!combined &&
          afterClaim.sms_available &&
          toggleRow({
            id: "toggle-sms",
            icon: glyph.message,
            title: `Text me at ${afterClaim.sms_phone}`,
            // Rendered verbatim from the backend, which stores this exact text as consent evidence.
            detail: afterClaim.sms_opt_in_language,
            checked: smsOptedIn,
            onChange: toggleSms,
          })}
        {methodSaved && (
          <a className="np-toggle-link" href="/profile">
            Manage how you receive money
            <span aria-hidden="true">›</span>
          </a>
        )}
      </div>
    );
  };

  const getIframeUrl = () => {
    const domain = window.location.host;
    if (domain === "localhost:5100") {
      return "http://localhost:3000";
    }
    if (domain === "receive.staging.neuralpayments.com") {
      return "https://p2c.app.staging.neuralpayments.com";
    }
    if (domain === "receive.neuralpayments.com") {
      return "https://p2c.app.neuralpayments.com";
    }
    if (domain === "np-dev-app-frontend.ngrok.io") {
      return "https://np-dev-app-frontend-cde.ngrok.io";
    }
    return "https://p2c.app.neuralpayments.com";
  };

  // `app=p2c` for regular send flows; `app=rfp` tells the CDE iframe to render
  // the debit-card entry UI ("Pay $X to Y") instead of the credit entry.
  const appMode = isRequestForPay ? "rfp" : "p2c";
  const cdeAppUrl = (app, extra = "") => `${getIframeUrl()}?t=${transferId}&i=${inviteId}&off_network_bg_primary=${branding.receiving_colors.receiving_bg_primary?.slice(
    1,
    7
  )}&off_network_bg_secondary=${branding.receiving_colors.receiving_bg_secondary?.slice(
    1,
    7
  )}&off_network_button_color=${branding.receiving_colors.receiving_bg_tertiary?.slice(
    1,
    7
  )}&off_network_text_color=${branding.receiving_colors.receiving_text?.slice(1, 7)}&app=${app}&ui_version=new&visa_direct_enabled=${branding.visa_direct_enabled || false}&mask_card_number=${branding.mask_card_number || false}&ui_refresh=${branding.enable_cde_ui_refresh === true}${extra}`;
  const iframeUrl = cdeAppUrl(appMode);

  // Amount card at the top of most screens. Deliberately contains no <h5>:
  // the Cypress suite reads `cy.get("h5").first()` for each screen's guidance
  // line, and it also asserts the RfP card never reads "sent $".
  const TransferDetails = () => {
    if (transferData.payorName) {
      transferData.sender = transferData.payorName;
      transferData.amount = transferData.amountNoDec / 100;
      transferData.note = transferData.claimFundsMessage;
    }
    const amountDisplay = `$${parseFloat(transferData.amount).toFixed(2)}`;
    const hasNote = transferData.note?.trim().length > 0;
    if (isRequestForPay) {
      return (
        <div className="np-amount-card">
          <div className="np-amount-lead">
            <b>{transferData.sender}</b> is requesting
          </div>
          <div className="np-amount-value">{amountDisplay}</div>
          <div className="np-amount-note">
            from you
            {hasNote && <> — "{transferData.note}".</>}
          </div>
        </div>
      );
    }
    return (
      <div className="np-amount-card">
        <div className="np-amount-lead">
          <b>{transferData.sender}</b> sent you
        </div>
        <div className="np-amount-value">{amountDisplay}</div>
        {hasNote && (
          <div className="np-amount-note">
            with the note: "{transferData.note}".
          </div>
        )}
      </div>
    );
  };

  const getFailedStateMsg = () => {
    if (["pushtocard", "googlepay", "applepay"].includes(paymentType)) {
      return (
        <h5>
          “Oh no... this card was not accepted at this time and the funds have
          been sent back to the sender. Let the sender know that they can resend
          the funds, and you can enter a different card or choose a different
          payment option.”
        </h5>
      );
    }

    return (
      <h5>
        Transfer failed on {statusDate.toLocaleDateString("en-US")} at{" "}
        {statusDate.toLocaleTimeString("en-US")}. The payment has been returned
        to the sender.
      </h5>
    );
  };

  const checkIndex = (page, index) => {
    switch (page) {
      case "homepage":
        return index > 0 && index < 10;
      case "optionspage":
        return index > 9;
      default:
        return null;
    }
  };

  const prizeout_params = {
    env: branding.prizeout_partner?.environment,
    user: {
      session_id: `${transferId}.${inviteId}`,
    },
    publisher: {
      id: branding.prizeout_partner?.partner_id,
    },
    callbacks: {
      onClose: () => {
        pollForTransferStatus();
      },
      onCashoutFail: () => {
        pollForTransferStatus();
      },
      onCashoutSuccess: () => {
        pollForTransferStatus();
      },
    },
  };

  const clickPayPal = () => {
    setPaymentType("paypal");
    setViewState("confirmPayPal");
  };

  const clickVenmo = () => {
    setPaymentType("venmo");
    if ("email" === guessRecipientType(recipientIdentifier)) {
      setViewState("changeVenmoIdentifier");
    } else {
      setViewState("confirmVenmo");
    }
  };

  const clickP2C = () => {
    setPaymentType("pushtocard");
    setViewState("changeP2CCard");
  };

  const clickP2A = () => {
    setPaymentType("pushtoaccount");
    setViewState("changeP2AAccount");
    setRecipientType("routing_account");
  };

  const clickDecline = async () => {
    setViewState("confirmDecline");
  };

  const clickPrizeout = () => {
    window.prizeoutSDK.init(prizeout_params);
  };

  const clickCoinbase = () => {
    if (parseFloat(transferData.amount) < 1.0) {
      setCoinbaseError("This payout method is unavailable for transfers below $1.00.");
      return;
    }
    setCoinbaseError("");
    setPaymentType("coinbase");
    setViewState("changeCoinbaseRecipient");
  };

  const knownCardIndex = branding.button_indexes.known_card_index;
  useEffect(() => {
    if (
      !(knownCardIndex > 0) ||
      isRequestForPay ||
      knownAccountCheckedRef.current ||
      !["default", "oneTap"].includes(viewState)
    ) {
      return;
    }
    knownAccountCheckedRef.current = true;
    api
      .get(`/external/transfer/${transferId}/${inviteId}/known-account`)
      .then((response) => setKnownAccount(response.data))
      .catch(() => {
        // 404: no known card for this recipient — the option just doesn't show.
      });
  }, [knownCardIndex, isRequestForPay, viewState, api, transferId, inviteId]);

  const knownAccountLabel = knownAccount
    ? `${knownAccount.fi_name} card ending ${knownAccount.card_last4}`
    : "";

  const acceptKnownAccount = async () => {
    setLoading(true);
    try {
      await api.post(`/external/transfer/${transferId}/${inviteId}/known-account/accept`);
      setPaymentType("pushtocard");
      setStatusDate(new Date());
      setViewState("processing");
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Wallet payouts run entirely in the CDE app (neuralpayments-app-frontend-cde): the wallet
  // token is encrypted to the CDE and only the CDE app ever handles it, like card entry.
  const googlePayIndex = branding.button_indexes.google_pay_index;
  // Apple Pay's "receive money" sheet needs Safari with Apple Pay on an iPhone (iOS 18+) or Mac
  // (macOS 15+). iPadOS shows an ordinary "Pay" sheet that balance-checks the recipient's card,
  // so hide the row there (iPadOS reports itself as a Mac; touch support gives it away).
  const applePayReceiveSupported = (() => {
    if (!window.ApplePaySession) return false;
    const ua = navigator.userAgent || "";
    const isIpad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    return !isIpad;
  })();
  const applePayIndex = applePayReceiveSupported ? branding.button_indexes.apple_pay_index : -1;

  // Google Pay works inside an iframe, so it opens in place like "Direct to Debit Card".
  const clickGooglePay = () => {
    setPaymentType("googlepay");
    setViewState("googlePayFrame");
  };

  // Apple Pay only completes when the address-bar domain is the one verified for our Merchant
  // ID, and claim links usually run inside the FI shortlink's frame. So send the whole tab to
  // the CDE app's Apple Pay page; it sends the recipient back here when it's done. Needs the
  // tap's user gesture for a cross-origin frame to navigate the top window.
  const clickApplePay = () => {
    const url = cdeAppUrl(
      "applepay",
      `&amount=${encodeURIComponent(parseFloat(transferData.amount).toFixed(2))}`
    );
    try {
      window.top.location.href = url;
    } catch (err) {
      window.location.href = url;
    }
  };

  // The "Enabled by Visa" lockup as a standalone centered block, for screens that aren't a
  // button. The in-button version floats off the label's right edge and is styled inline there.
  // h5 carries `margin: 15px 0 40px`, so without a negative top margin the lockup lands 40px
  // below the text with an obvious gap. -28px leaves it sitting just underneath.
  //
  // Uses the 2-line lockup, which Visa calls the preferred version; there is room for it here
  // because this renders as a standalone centered block rather than beside a button label.
  //
  // Height is 48px rather than ~30 because Visa bakes their minimum clear space into the
  // viewBox as a 30-unit inset: the artwork occupies only 109.35 of the 169.35 box height, so
  // roughly 65%. Sizing to the box gives a mark of about 31px. Sizing it as if the box were
  // the artwork — which is what the old, tightly-cropped PNG allowed — is what made this look
  // undersized.
  // 72px is the element box, not the mark — the clear-space inset above means the visible mark
  // reads about 46px. Processing and confirmation share one size deliberately, so the
  // endorsement doesn't appear to change scale as the recipient moves between the two screens.
  const renderVisaEndorsement = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        margin: "4px 0 16px",
      }}
    >
      <img
        src="/enabledbyVisa_2line_bluRGB_2025.svg"
        alt="Enabled by Visa"
        style={{ height: "72px" }}
      />
    </div>
  );

  /**
   * Contents of a "Send to <wallet>" button: the label plus the Visa endorsement.
   *
   * The lockup is absolutely positioned at `left: 100%`, deliberately outside the label's own
   * box. That is what lets the *label* centre on exactly the same axis as "Direct to Debit
   * Card" and the other payout buttons, instead of the label-plus-lockup pair centring as a
   * unit and dragging the label off to the left.
   *
   * `renderButton` lays out three flex children under `space-between`: a 32px icon, the title,
   * and a 32px spacer on the right. The spacer is what centres the title. So the title must be
   * an ordinary auto-width item — give it `flex: 1` and it swallows the middle instead, which is
   * how the label ended up ~60px left of its siblings. Measured in the browser: siblings' labels
   * begin at x≈845-856 on an 1800px button (centre 920), and the flex: 1 version began at 787.
   *
   * Shared by the homepage button and the confirm-screen button so the two cannot drift — which
   * is also what Visa's Aug 2026 review asked for.
   */
  const renderVisaAliasButtonLabel = (directory) => (
    <span style={{ position: "relative", display: "inline-block" }}>
      Send to {visaAliasMeta(directory).name}
      {/* 1-line lockup, not the preferred 2-line: Visa supplies it "for when space is limited,
          such as on smaller device screens", which is exactly this slot.
          28px, and no margin — at this height the asset's own 30-unit inset already supplies
          ~5px of clear space, and the lockup is ~94px wide, which is what fits in the room to
          the right of a centred label on a 375px-wide screen. Larger overflows the button. */}
      <img
        src="/enabledbyVisa_1line_bluRGB_2025.svg"
        alt="Enabled by Visa"
        style={{
          height: "28px",
          position: "absolute",
          left: "100%",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      />
    </span>
  );

  // Visa Alias directories: display metadata per internal directory key. New wallets
  // (venmo, paypal, ...) get a button automatically once branding enables them; add an
  // entry here to give them a proper name/icon.
  //
  const visaAliasMeta = (dir) => {
    switch (dir) {
      case "cashapp":
        return {
          name: "Cash App",
          icon: (
            <CashAppSymbol
              role="img"
              aria-label="Cash App"
              style={{ width: "32px", height: "32px" }}
            />
          ),
        };
      default:
        return {
          name: dir ? dir.charAt(0).toUpperCase() + dir.slice(1) : "Visa Alias",
          icon: false,
        };
    }
  };

  // Visa Alias (Visa Alias Directory Service): recipient enters phone/email, we resolve
  // it to the recipient's name (and, server-side, a payment token) and pay to the wallet
  // for the chosen directory (Cash App today).
  const clickVisaAlias = (directory) => {
    setPaymentType("visaalias");
    setVisaAliasDirectory(directory);
    setErrorIdentifier(false);
    setVisaAliasError("");
    setVisaAliasDetails({ firstName: "", lastName: "", phone: "" });
    setErrorVisaAliasFirstName(false);
    setErrorVisaAliasLastName(false);
    setErrorVisaAliasPhone(false);
    // Don't clear recipientIdentifier — it backs the homepage "sent to <id>" display and
    // pre-fills the input (the recipient's phone/email is usually their alias too).
    setViewState("visaAliasEnter");
  };

  // Verify the recipient against the wallet's record for this alias before letting them
  // commit. The backend runs Visa's Inquiry → Confirm, checks the last 4 against the wallet's
  // contact number, and name-matches what the recipient typed against the profile Confirm
  // returns. It answers with a verdict only — the wallet's name-on-file is never sent here,
  // and the payment token is captured server-side at store-input time.
  const resolveVisaAlias = async () => {
    setErrorIdentifier(false);
    setVisaAliasError("");
    if (!confirmVisaAliasIdentifier()) {
      return;
    }
    if (!confirmVisaAliasDetails()) {
      return;
    }
    setLoading(true);
    const aliasType = visaAliasKind();
    const walletName = visaAliasMeta(visaAliasDirectory).name;
    try {
      const response = await api.post(
        `/external/transfer/${transferId}/${inviteId}/resolve`,
        {
          alias_type: aliasType,
          alias_value: recipientIdentifier.trim(),
          directory: visaAliasDirectory || null,
          first_name: visaAliasDetails.firstName.trim(),
          last_name: visaAliasDetails.lastName.trim(),
          contact_number_last4: visaAliasLast4(),
        }
      );
      const { status, attempts_remaining } = response.data || {};

      // Every press of Next consumes one of the three verification attempts, whatever the
      // outcome. Each status below supplies only the *reason* it failed; what to do next is
      // decided here, from the attempts left. Keeping them separate is deliberate — the reason
      // used to carry its own "try again", so when the out-of-tries sentence was appended the
      // recipient got told to retry and to stop retrying in the same breath.
      const whatNext =
        typeof attempts_remaining !== "number"
          ? " Please double-check and try again."
          : attempts_remaining > 1
          ? ` Please double-check and try again — you have ${attempts_remaining} attempts left.`
          : attempts_remaining === 1
          ? " Please double-check and try again — this is your last attempt."
          : " You're out of attempts, so please choose a different way to get paid.";

      switch (status) {
        case "verified":
          setViewState("visaAliasConfirm");
          break;
        case "wallet_action_required":
          setVisaAliasAttemptsLeft(
            typeof attempts_remaining === "number" ? attempts_remaining : null
          );
          // The alias is valid but the recipient has something to finish at their wallet
          // (KYC, terms, no card issued). Deliberately its own screen rather than the
          // generic not-found copy — telling them the alias doesn't exist is what Visa
          // asked us to stop doing.
          setViewState("visaAliasWalletAction");
          break;
        case "locked_out":
          setVisaAliasError(
            `For your security, we can't verify any more attempts for this payment. Please choose a different way to get paid.`
          );
          break;
        case "wrong_directory":
          // The alias exists at some other wallet. Which one is never named, and the backend no
          // longer sends it: it would tell anyone holding a phone number or email address which
          // wallet that person banks with, and the recipient's remedy is the same either way.
          //
          // Its own next-step, with no attempts sentence: the alias is fine, the wallet choice
          // isn't, so retrying the same details here would fail identically.
          setVisaAliasError(
            `That contact isn't registered with ${walletName}. Please go back and choose a different way to get paid.`
          );
          break;
        case "not_found":
          setVisaAliasError(
            `We couldn't find a ${walletName} account for that.${whatNext}`
          );
          break;
        case "mismatch":
        default: {
          // One generic message covering both a wrong last 4 and a name that didn't match.
          // Naming the field that failed would let someone probe them one at a time.
          setVisaAliasError(
            `Those details don't match the ${walletName} account for that contact.${whatNext}`
          );
          break;
        }
      }
    } catch (err) {
      setVisaAliasError(
        `We couldn't verify your ${walletName} account right now. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const prizeoutEnabled = () => {
    if (
      branding.button_indexes.prizeout_index > 0 &&
      branding.prizeout_partner &&
      parseFloat(transferData.amount) < 500.01
    ) {
      return true;
    }
  };

  if (
    prizeoutEnabled() &&
    branding.button_indexes.prizeout_widget &&
    ((viewState === "default" &&
      checkIndex("homepage", branding.button_indexes.prizeout_index)) ||
      (viewState === "moreOptions" &&
        checkIndex("optionspage", branding.button_indexes.prizeout_index)))
  ) {
    setTimeout(() => {
      window.prizeoutSDK.create_widget(
        document.getElementById("mini-widget"),
        prizeout_params
      );
    }, 200);
  }

  /**
   * Payout chooser. `branding.button_indexes` still decides which options
   * render on which page (`checkIndex`) and in what order; entries are sorted
   * by index and consecutive payout rows are grouped into one white card,
   * with standalone actions (Decline, Gift Card, More Options, Go Back, the
   * Prizeout widget) rendered as full-width buttons in that same order.
   */
  const renderButtons = (page) => {
    const items = [];

    const addRow = (key, title, index, onclick, icon) => {
      if (checkIndex(page, index)) {
        items.push({
          kind: "row",
          index,
          el: (
            <button
              key={key}
              type="button"
              className="np-row-btn"
              onClick={onclick}
            >
              <span className="np-avatar">{icon}</span>
              <span className="np-row-btn-label">{title}</span>
              {glyph.chevron}
            </button>
          ),
        });
      }
    };

    const addAction = (key, index, el) => {
      items.push({ kind: "action", index, el: <div key={key}>{el}</div> });
    };

    if (knownAccount) {
      addRow(
        "knowncard",
        `Deposit to your ${knownAccountLabel}`,
        knownCardIndex,
        () => setViewState("knownAccountConfirm"),
        knownAccount.fi_logo_url ? (
          <img className="np-known-logo" src={knownAccount.fi_logo_url} alt="" />
        ) : (
          <CardIcon className="np-glyph-stroke" />
        )
      );
    }
    addRow(
      "paypal",
      "PayPal",
      branding.button_indexes.paypal_index,
      clickPayPal,
      <PayPalIcon style={{ width: "20px", height: "20px" }} />
    );
    addRow(
      "venmo",
      "Venmo",
      branding.button_indexes.venmo_index,
      clickVenmo,
      <VenmoIcon style={{ width: "20px", height: "20px" }} />
    );
    addRow(
      "p2c",
      "Direct to Debit Card",
      branding.button_indexes.p2c_index,
      clickP2C,
      <CardIcon className="np-glyph-stroke" />
    );
    addRow(
      "applepay",
      "Apple Pay",
      applePayIndex,
      clickApplePay,
      <AppleIcon style={{ width: "20px", height: "20px" }} />
    );
    addRow(
      "googlepay",
      "Google Pay",
      googlePayIndex,
      clickGooglePay,
      <GoogleIcon style={{ width: "20px", height: "20px" }} />
    );
    addRow(
      "p2a",
      "Direct to Account",
      branding.button_indexes.p2a_index,
      clickP2A,
      <BankIcon className="np-glyph-fill" />
    );
    addRow(
      "coinbase",
      "Send to Coinbase",
      branding.button_indexes.coinbase_index,
      clickCoinbase,
      <CoinbaseIcon
        style={{ width: "32px", height: "32px", borderRadius: "50%" }}
      />
    );
    Object.entries(branding.button_indexes.visa_alias || {}).forEach(
      ([directory, index]) =>
        addRow(
          `visa_alias_${directory}`,
          // Label plus the "Enabled by Visa" lockup (Visa-supplied asset,
          // unmodified). Shared with the confirm-screen button — see
          // renderVisaAliasButtonLabel.
          renderVisaAliasButtonLabel(directory),
          index,
          () => clickVisaAlias(directory),
          visaAliasMeta(directory).icon
        )
    );

    if (checkIndex(page, branding.button_indexes.decline_index)) {
      addAction(
        "decline",
        branding.button_indexes.decline_index,
        <button type="button" className="np-btn-quiet" onClick={clickDecline}>
          Decline Payment
        </button>
      );
    }

    if (
      prizeoutEnabled() &&
      checkIndex(page, branding.button_indexes.prizeout_index)
    ) {
      addAction(
        "prizeout",
        branding.button_indexes.prizeout_index,
        branding.button_indexes.prizeout_widget ? (
          <div
            id="mini-widget"
            style={{
              height: "220px",
              marginBottom: "12px",
              borderRadius: "18px",
              padding: "4px",
              backgroundColor: "#ffffff",
              boxShadow:
                "0 1px 2px rgba(16,18,22,0.05), 0 10px 28px rgba(16,18,22,0.05)",
            }}
          ></div>
        ) : (
          <button type="button" onClick={clickPrizeout}>
            <GiftCardIcon
              className="np-glyph-fill"
              style={{ width: "22px", height: "22px" }}
            />
            Gift Card
          </button>
        )
      );
    }

    if (page === "homepage") {
      if (
        branding.button_indexes.paypal_index > 9 ||
        branding.button_indexes.venmo_index > 9 ||
        branding.button_indexes.p2c_index > 9 ||
        branding.button_indexes.p2a_index > 9 ||
        branding.button_indexes.prizeout_index > 9 ||
        branding.button_indexes.coinbase_index > 9 ||
        googlePayIndex > 9 ||
        applePayIndex > 9 ||
        branding.button_indexes.decline_index > 9
      ) {
        addAction(
          "more",
          10,
          <button type="button" onClick={() => setViewState("moreOptions")}>
            More Options
          </button>
        );
      }
    } else {
      addAction(
        "back",
        20,
        <button type="button" onClick={returnHome}>
          Go Back
        </button>
      );
    }

    items.sort((x, y) => x.index - y.index);

    // Group consecutive rows into one card.
    const groups = [];
    items.forEach((item) => {
      const last = groups[groups.length - 1];
      if (item.kind === "row" && last && last.kind === "row") {
        last.els.push(item.el);
      } else {
        groups.push({ kind: item.kind, els: [item.el] });
      }
    });

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {groups.map((group, i) =>
          group.kind === "row" ? (
            <div
              key={`card_${i}`}
              className="np-card"
              style={{ marginBottom: "14px" }}
            >
              {group.els}
            </div>
          ) : (
            <React.Fragment key={`actions_${i}`}>{group.els}</React.Fragment>
          )
        )}
      </div>
    );
  };

  const renderAdvertising = () => {
    const renderAdvertisingButton = () => {
      let link = branding.advertising.advertising_link;

      if (!branding.advertising.advertising_link?.includes("http")) {
        link = `https://${branding.advertising.advertising_link}`;
      }
      return (
        <>
          {branding.advertising.advertising_button_text && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none" }}
              >
                {branding.advertising.advertising_button_text}
              </a>
            </div>
          )}
        </>
      );
    };

    return (
      <>
        {branding.advertising.advertising_description ? (
          <div className="advertising_box">
            <div>{branding.advertising.advertising_description}</div>
            {renderAdvertisingButton()}
          </div>
        ) : (
          <div style={{ margin: "40px 0" }}>{renderAdvertisingButton()}</div>
        )}
      </>
    );
  };

  const renderP2AInfo = () => {
    return (
      <div className="np-card" style={{ margin: "12px 0 8px" }}>
        <div className="np-detail-row">
          <span className="np-detail-label">Routing number</span>
          <span className="np-detail-value">
            {recipientAccountDetails.routing_number}
          </span>
        </div>
        <div className="np-detail-row">
          <span className="np-detail-label">Account number</span>
          <span className="np-detail-value">
            {
              // TODO successful or processing?
              viewState === "successful"
                ? recipientAccountDetails.account_number.replace(
                    /^.{5}/g,
                    "*****"
                  )
                : recipientAccountDetails.account_number
            }
          </span>
        </div>
      </div>
    );
  };

  const renderP2AGoBack = () => {
    return (
      <div>
        <button
          onClick={() => {
            setRecipientType("");
            setRecipientIdentifier(
              transferData.recipient_identifier || transferData.payeeEmail
            );
            setRecipientAccountDetails({
              routing_number: "",
              account_number: "",
              firstName: "",
              lastName: "",
            });
            setErrorBankAccount(false);
            setErrorBankRouting(false);
            returnHome();
          }}
        >
          Go Back
        </button>
      </div>
    );
  };

  const changeIdentifier = (state) => {
    return (
      <>
        {TransferDetails()}
        <h5>
          Please choose an alternate {getPaymentTypeDisplay()} account that can
          receive funds.
        </h5>
        <label htmlFor="recipient_identifier">
          {state === "confirmPayPal" ? "Phone number or email" : "Phone number"}
        </label>
        <input
          id="recipient_identifier"
          value={recipientIdentifier}
          onChange={(e) => handleRecipientIdentifierChange(e)}
          placeholder="Type Account Number"
          disabled={loading}
        />
        {errorIdentifier && (
          <div className="np-error">
            {state === "confirmPayPal"
              ? "Please enter a valid email or phone number"
              : "Please enter a valid phone number"}
          </div>
        )}
        <button
          className="button_primary"
          onClick={() => {
            if (confirmIdentifier()) {
              setViewState(state);
            }
          }}
        >
          Confirm
        </button>
        <button
          onClick={() => {
            setRecipientType("");
            setRecipientIdentifier(
              transferData.recipient_identifier || transferData.payeeEmail
            );
            returnHome();
          }}
        >
          Go Back
        </button>
      </>
    );
  };

  const confirmType = (state) => {
    const textDisplay = () => {
      switch (state) {
        case "changePayPalIdentifier":
          return "Choose an alternate email or US phone number";
        case "changeVenmoIdentifier":
          return "Choose an alternate US phone number";
        default:
          return "";
      }
    };
    return (
      <>
        {TransferDetails()}
        <h5>
          Please confirm that your {getPaymentTypeDisplay()} account can receive
          funds sent to {getRecipientIdentifierDisplay()}.
        </h5>
        {errorIdentifier && (
          <div className="np-error">
            {state === "confirmPayPal"
              ? "Please enter a valid email or phone number"
              : "Please enter a valid phone number"}
          </div>
        )}
        <button onClick={submitTransfer} className="button_primary">
          Send Payment via {getPaymentTypeDisplay()}
        </button>
        <button
          onClick={() => {
            setRecipientType("email");
            setViewState(state);
          }}
        >
          {textDisplay()}
        </button>
        <button onClick={returnHome}>Go Back</button>
      </>
    );
  };

  const renderScreen = () => {
    switch (true) {
      case error:
        return <>Sorry, an error has occurred.</>;
      // Transfer still loading — render nothing rather than an amount card
      // built from empty transferData ("$NaN").
      case !viewState:
        return null;
      case "successful" === viewState:
        return (
          <>
            <div className="np-hero">
              <span className="np-hero-icon np-hero-icon-accent">
                {glyph.check}
              </span>
              <h2 className="np-h">
                {isRequestForPay
                  ? "Payment sent"
                  : `$${parseFloat(transferData.amount).toFixed(2)} is on its way`}
              </h2>
              <h5>
                {isRequestForPay ? (
                  // RfP: the requestee (this iframe user) paid the requester.
                  // Show payment going TO the requester (transferData.sender =
                  // the person who initiated the request on the platform).
                  <>
                    Payment of <b>${parseFloat(transferData.amount).toFixed(2)}</b> was sent to <b>{transferData.sender}</b>
                    {getPaymentTypeDisplay() && (
                      <> via {getPaymentTypeDisplay()}</>
                    )}
                  </>
                ) : paymentType === "visaalias" ? (
                  // Names the wallet and the alias it went to, rather than the generic
                  // "to <id> via <type>" wording every other rail uses.
                  <>
                    Payment was sent to {visaAliasMeta(visaAliasDirectory).name}{" "}
                    alias {getRecipientIdentifierDisplay()}
                  </>
                ) : paymentType !== "pushtoaccount" ? (
                  <>
                    Payment was sent
                    {getRecipientIdentifierDisplay() && (
                      <> to {getRecipientIdentifierDisplay()}</>
                    )}
                    {getPaymentTypeDisplay() && (
                      <> via {getPaymentTypeDisplay()}</>
                    )}
                  </>
                ) : (
                  <>Transfer initiated to {renderP2AInfo()}</>
                )}{" "}
                on {statusDate.toLocaleDateString("en-US")} at{" "}
                {statusDate.toLocaleTimeString("en-US")}
              </h5>
            </div>
            {/* Visa asked for the endorsement on the confirmation screen as well as processing
                (Aug 2026 UX review). Scoped to visaalias so no other rail shows a Visa lockup.
                Same size as on processing, so it doesn't appear to change scale between them. */}
            {paymentType === "visaalias" && renderVisaEndorsement()}
            {renderAfterClaimOptions()}
            {renderAdvertising()}
            {isRequestForPay ? (
              <p className="np-h-sub">You may now close this window.</p>
            ) : (
              paymentType !== "prizeoutcard" && (
                <p className="np-h-sub">
                  You may now close this window and check your account for the
                  funds.
                </p>
              )
            )}
          </>
        );
      case "confirmDecline" === viewState:
        return (
          <>
            {renderHomepage()}
            <div
              className="np-dialog-backdrop"
              role="dialog"
              aria-modal="true"
              aria-labelledby="decline-title"
            >
              <div className="np-dialog">
                <h2 id="decline-title">Decline this payment?</h2>
                <p>
                  The ${parseFloat(transferData.amount).toFixed(2)} will be
                  returned to{" "}
                  {transferData.sender || transferData.payorName || "the sender"}
                  . This can't be undone.
                </p>
                <button
                  type="button"
                  className="np-btn-danger"
                  onClick={deleteTransfer}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="button_spinner" aria-label="Loading" />
                  ) : (
                    "Yes, decline payment"
                  )}
                </button>
                <button
                  type="button"
                  onClick={backOutDeleteTransfer}
                  disabled={loading}
                >
                  Keep it
                </button>
              </div>
            </div>
          </>
        );
      case "declined" === viewState:
        return (
          <>
            <div className="np-hero">
              <span className="np-hero-icon np-hero-icon-quiet">
                {glyph.cross}
              </span>
              <h2 className="np-h">Payment declined</h2>
              <h5>
                Payment was declined on {statusDate.toLocaleDateString("en-US")}{" "}
                at {statusDate.toLocaleTimeString("en-US")}. Funds will be
                returned to the sender.
              </h5>
            </div>
            {renderAdvertising()}
          </>
        );
      case "failed" === viewState:
        return (
          <>
            <div className="np-hero">
              <span className="np-hero-icon np-hero-icon-quiet">
                {glyph.cross}
              </span>
              <h2 className="np-h">Payment failed</h2>
              {getFailedStateMsg()}
            </div>
            {renderAdvertising()}
          </>
        );
      case "processing" === viewState:
        return (
          <>
            <div className="np-hero" style={{ paddingTop: "46px" }}>
              <span className="np-hero-icon">{glyph.clock}</span>
              <h2 className="np-h">
                {isRequestForPay ? "Sending your payment" : "Receiving your money"}
              </h2>
              {paymentType === "coinbase" &&
              (recipientType === "walletaddress" ||
                recipientType === "coinbase") ? (
                <h5>
                  Your payment has been submitted to the blockchain. This may
                  take 15–30 minutes depending on network conditions. You can
                  safely close this page — your funds are on the way.
                </h5>
              ) : (
                <h5>Payment is processing...</h5>
              )}
              <div className="np-progress" aria-hidden="true">
                <span />
              </div>
            </div>
            {/* Visa requires the endorsement on the alias flow's screens. Scoped to visaalias so
                no other payment type picks up a Visa lockup. */}
            {paymentType === "visaalias" && renderVisaEndorsement()}
          </>
        );
      case "visaSensory" === viewState:
        // The Visa Sensory Branding animation, between processing and confirmation. Nothing
        // else renders: Visa's guide says full-screen mode is "best to have no or very few
        // other elements on the screen". The div is where the SDK mounts its iframe; the
        // effect above drives it and advances to `successful` when it ends.
        return <div id="visa-sensory-branding" />;
      case "validating" === viewState:
        return (
          <div className="np-hero" style={{ paddingTop: "46px" }}>
            <span className="np-hero-icon">{glyph.clock}</span>
            <h2 className="np-h">One moment</h2>
            <h5>Validating...</h5>
            <div className="np-progress" aria-hidden="true">
              <span />
            </div>
          </div>
        );
      case "not_found" === viewState:
        return (
          <div className="np-hero" style={{ paddingTop: "60px" }}>
            <span className="np-hero-icon np-hero-icon-quiet">
              {glyph.search}
            </span>
            <h5 className="np-h">Transfer not found.</h5>
          </div>
        );
      case "server_down" === viewState:
        return (
          <div className="np-hero" style={{ paddingTop: "60px" }}>
            <span className="np-hero-icon np-hero-icon-quiet">
              {glyph.cross}
            </span>
            <h5 className="np-h" style={{ fontSize: "22px" }}>
              This service is currently down for maintenance. We apologize for
              the inconvenience.
            </h5>
          </div>
        );
      case "changePayPalIdentifier" === viewState:
        return changeIdentifier("confirmPayPal");
      case "changeVenmoIdentifier" === viewState:
        return changeIdentifier("confirmVenmo");
      case "confirmPayPal" === viewState:
        return confirmType("changePayPalIdentifier");
      case "confirmVenmo" === viewState:
        return confirmType("changeVenmoIdentifier");
        case "confirmP2A_no_service" === viewState:
          return (
            <>
              {TransferDetails()}
              <h5>
                <p style={{ marginBottom: "20px", maxWidth: "500px", display: "inline-block" }}>
                  {getPaymentTypeDisplay()} is not available at this time.
                  Please choose another payment method.
                </p>
              {renderP2AInfo()}
              </h5>
              <button
                onClick={() => {
                  setViewState("changeP2AAccount");
                }}
              >
                Choose an alternate account
              </button>
              {renderP2AGoBack()}
            </>
          );
      case "confirmP2A_no_receive" === viewState:
          return (
            <>
              {TransferDetails()}
              <h5>
                <p style={{ marginBottom: "20px", maxWidth: "500px", display: "inline-block" }}>
                  Unfortunately, {getPaymentTypeDisplay()} is not supported for
                  this account. Please choose another payment method.
                </p>
              {renderP2AInfo()}
              </h5>
              <button
                onClick={() => {
                  setViewState("changeP2AAccount");
                }}
              >
                Choose an alternate account
              </button>
              {renderP2AGoBack()}
            </>
          );
          case "confirmP2A_no_instant_transfer" === viewState:
            return (
              <>
                {TransferDetails()}
                <h5>
                  <p style={{ marginBottom: "20px", maxWidth: "500px", display: "inline-block" }}>
                    This account can receive funds via {getPaymentTypeDisplay()}.
                    However, it may take up to 3 days to process.
                    You may choose to proceed, choose an alternate account, or
                    choose another payment method.
                  </p>
                {renderP2AInfo()}
                </h5>
                <button onClick={submitTransfer} className="button_primary">
                  Send Payment via {getPaymentTypeDisplay()} <br/>(may take up to 3 days)
                </button>
                <button
                  onClick={() => {
                    setViewState("changeP2AAccount");
                  }}
                >
                  Choose an alternate account
                </button>
                {renderP2AGoBack()}
              </>
            );
      case "confirmP2A" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>
              <p style={{ marginBottom: "20px", maxWidth: "500px", display: "inline-block" }}>
                Please confirm your information is correct. Funds
                will be sent immediately via {getPaymentTypeDisplay()}.
              </p>
            {renderP2AInfo()}
            </h5>
            <button onClick={submitTransfer} className="button_primary">
              Send Payment via {getPaymentTypeDisplay()}
            </button>
            <button
              onClick={() => {
                setViewState("changeP2AAccount");
              }}
            >
              Choose an alternate account
            </button>
            {renderP2AGoBack()}
          </>
        );
      case "changeCoinbaseRecipient" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>How would you like to receive your Coinbase payment?</h5>
            <button
              onClick={() => {
                setRecipientType("email");
                setViewState("changeCoinbaseEmail");
              }}
            >
              Send to Coinbase Email
            </button>
            <button
              onClick={() => {
                setRecipientType("walletaddress");
                setRecipientIdentifier("");
                setViewState("changeCoinbaseWallet");
              }}
            >
              Send to Wallet Address
            </button>
            <button onClick={returnHome}>Go Back</button>
          </>
        );
      case "changeCoinbaseEmail" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>
              Please enter the email associated with your Coinbase account.
            </h5>
            <label htmlFor="recipient_identifier">Coinbase Email</label>
            <input
              id="recipient_identifier"
              value={recipientIdentifier}
              onChange={(e) => handleRecipientIdentifierChange(e)}
              placeholder="Enter email address"
              disabled={loading}
            />
            {errorIdentifier && (
              <div style={{ marginBottom: "20px" }}>
                Please enter a valid email address
              </div>
            )}
            <button
              className="button_primary"
              onClick={() => {
                if (confirmIdentifier()) {
                  setViewState("confirmCoinbaseEmail");
                }
              }}
            >
              Confirm
            </button>
            <button
              onClick={() => {
                setRecipientIdentifier(
                  transferData.recipient_identifier || transferData.payeeEmail
                );
                setViewState("changeCoinbaseRecipient");
              }}
            >
              Go Back
            </button>
          </>
        );
      case "confirmCoinbaseEmail" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>
              Please confirm that your Coinbase account can receive
              funds sent to {getRecipientIdentifierDisplay()}.
            </h5>
            <button onClick={submitTransfer} className="button_primary">
              Send Payment via {getPaymentTypeDisplay()}
            </button>
            <button
              onClick={() => {
                setViewState("changeCoinbaseEmail");
              }}
            >
              Choose an alternate email
            </button>
            <button onClick={returnHome}>Go Back</button>
          </>
        );
      case "changeCoinbaseWallet" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>
              Please enter your crypto wallet address.
            </h5>
            <label htmlFor="recipient_identifier">Wallet Address</label>
            <input
              id="recipient_identifier"
              value={recipientIdentifier}
              onChange={(e) => handleRecipientIdentifierChange(e)}
              placeholder="Enter wallet address (e.g. 0x... or bc1q...)"
              disabled={loading}
            />
            {errorIdentifier && (
              <div style={{ marginBottom: "20px" }}>
                Please enter a valid wallet address
              </div>
            )}
            <button
              className="button_primary"
              onClick={() => {
                if (recipientIdentifier.length >= 20) {
                  setErrorIdentifier(false);
                  setRecipientType("walletaddress");
                  setViewState("confirmCoinbaseWallet");
                } else {
                  setErrorIdentifier("Please enter a valid wallet address");
                }
              }}
            >
              Confirm
            </button>
            <button
              onClick={() => {
                setRecipientIdentifier(
                  transferData.recipient_identifier || transferData.payeeEmail
                );
                setViewState("changeCoinbaseRecipient");
              }}
            >
              Go Back
            </button>
          </>
        );
      case "confirmCoinbaseWallet" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>
              Please confirm that this wallet address is correct:{" "}
              <b style={{ wordBreak: "break-all" }}>{recipientIdentifier}</b>
            </h5>
            <button onClick={submitTransfer} className="button_primary">
              Send Payment via {getPaymentTypeDisplay()}
            </button>
            <button
              onClick={() => {
                setViewState("changeCoinbaseWallet");
              }}
            >
              Choose a different wallet address
            </button>
            <button onClick={returnHome}>Go Back</button>
          </>
        );
      case "moreOptions" === viewState:
        return (
          <>
            {TransferDetails()}
            <h2 className="np-section">How would you like to get paid?</h2>
            {coinbaseError && <div className="np-error">{coinbaseError}</div>}
            {renderButtons("optionspage")}
          </>
        );
      case "knownAccountConfirm" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5 className="np-h">Deposit to your {knownAccountLabel}?</h5>
            <button
              id="known-account-accept"
              onClick={acceptKnownAccount}
              disabled={loading}
              className="button_primary"
            >
              {loading ? (
                <span className="button_spinner" aria-label="Loading" />
              ) : (
                <>Deposit ${parseFloat(transferData.amount).toFixed(2)}</>
              )}
            </button>
            <button id="known-account-other" onClick={returnHome}>
              Choose another way
            </button>
          </>
        );
      case "oneTap" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5 className="np-h">Receive it to your {savedMethod?.label}?</h5>
            <p className="np-h-sub">
              You saved this the last time money came from this bank. Tap once to receive it.
            </p>
            <button
              id="one-tap-accept"
              onClick={acceptSavedMethod}
              disabled={loading}
              className="button_primary"
            >
              {loading ? (
                <span className="button_spinner" aria-label="Loading" />
              ) : (
                <>Receive to {savedMethod?.label}</>
              )}
            </button>
            <button id="one-tap-other" onClick={returnHome}>
              Use a different method
            </button>
            <p className="np-h-sub np-caption">
              <a href="/profile">Manage how you receive money</a>
            </p>
          </>
        );
      case "googlePayFrame" === viewState:
        // Google Pay runs in the CDE app (app=googlepay); allow="payment" lets the Google Pay
        // sheet open from inside the iframe. We keep polling the transfer, so this moves on to
        // "Receiving your money" once the CDE has the card.
        return (
          <>
            {TransferDetails()}
            <h5>Receive with Google Pay</h5>
            <div className="np-iframe-card">
              <iframe
                title="google-pay-iframe"
                id="cde-google-pay-iframe"
                style={{ border: "0" }}
                width="100%"
                height="260px"
                src={cdeAppUrl("googlepay")}
                allow="payment"
              ></iframe>
            </div>
            <button id="go-back-from-google-pay" onClick={returnHome}>
              Go Back
            </button>
          </>
        );
      case "changeP2CCard" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>{isRequestForPay ? "Send with Debit Card" : "Receive with Debit Card"}</h5>
            <div className="np-iframe-card">
            <iframe
              title={"debit-card-input-iframe"}
              id="cde-iframe"
              style={{ border: "0" }}
              width={"100%"}
              // RfP card entry adds 4 billing-address fields, so the form is
              // taller than the default 530px and would scroll inside the
              // iframe. Use a height tuned to fit the full RfP form (the p2c
              // 530px baseline + the 4 extra address fields). Once the transfer
              // completes (frameHeight -> "200px") fall back so the success view
              // doesn't leave a big empty frame.
              height={
                isRequestForPay && frameHeight !== "200px"
                  ? "880px"
                  : frameHeight
              }
              src={iframeUrl}
              allow=""
            ></iframe>
            </div>
            {/* Request for Pay: the requestee can only pay by debit card, so
                there is no payout chooser to go back to. */}
            {!isRequestForPay && (
              <button id="go-back-from-p2c" onClick={returnHome}>
                Go Back
              </button>
            )}
          </>
        );
      case "changeP2AAccount" === viewState:
        return (
          <>
            {TransferDetails()}
            {/* Stays an h5: the Cypress suite reads the first h5 for
                "Transfer to Account". */}
            <h5 className="np-h">Transfer to Account</h5>
            <p className="np-h-sub">
              Enter the account that should receive the funds.
            </p>
            <div className="np-card np-card-pad" style={{ marginBottom: "20px" }}>
              <CheckExample
                style={{ width: "100%", maxWidth: "320px", height: "auto", display: "block", margin: "0 auto" }}
              />
            </div>
            <div className="np-card" style={{ marginBottom: "20px" }}>
              <div className="np-row">
                <label htmlFor="firstName">First name</label>
                <input
                  id="firstName"
                  placeholder="First name"
                  value={recipientAccountDetails.firstName}
                  onChange={(e) => handleRecipientAccountDetailsChange(e)}
                  required
                  type="text"
                  disabled={loading}
                  aria-invalid={errorFirstName || undefined}
                />
              </div>
              {errorFirstName && (
                <div className="np-field-error">Please enter your first name</div>
              )}
              <div className="np-row">
                <label htmlFor="lastName">Last name</label>
                <input
                  id="lastName"
                  placeholder="Last name"
                  value={recipientAccountDetails.lastName}
                  onChange={(e) => handleRecipientAccountDetailsChange(e)}
                  required
                  type="text"
                  disabled={loading}
                  aria-invalid={errorLastName || undefined}
                />
              </div>
              {errorLastName && (
                <div className="np-field-error">Please enter your last name</div>
              )}
              <div className="np-row">
                <label htmlFor="routing_number">Routing</label>
                <input
                  id="routing_number"
                  placeholder="9 digits"
                  value={recipientAccountDetails.routing_number}
                  onChange={(e) => handleRecipientAccountDetailsChange(e)}
                  required
                  type="tel"
                  aria-invalid={errorBankRouting || undefined}
                />
              </div>
              {errorBankRouting && (
                <div className="np-field-error">
                  {/\D/.test(recipientAccountDetails.routing_number)
                    ? "Please enter only the digits of your routing number — no spaces, dashes, or periods."
                    : "Please enter a valid 9-digit routing number."}
                </div>
              )}
              <div className="np-row">
                <label htmlFor="account_number">Account</label>
                <input
                  id="account_number"
                  placeholder="Account number"
                  value={recipientAccountDetails.account_number}
                  onChange={(e) => handleRecipientAccountDetailsChange(e)}
                  required
                  type="tel"
                  disabled={loading}
                  aria-invalid={errorBankAccount || undefined}
                />
              </div>
              {errorBankAccount && (
                <div className="np-field-error">
                  Enter your full account number used for direct deposit or
                  electronic transactions such as ACH. To confirm your electronic
                  account number, visit your financial institution's online
                  banking portal or call your bank or credit union.
                </div>
              )}
            </div>
            <button
              className="button_primary"
              onClick={async () => {
                const isValid = await confirmBankAccount();
                if (isValid) {
                  setViewState("validating");
                  setRecipientIdentifier(
                    recipientAccountDetails.routing_number +
                      "_" +
                      recipientAccountDetails.account_number
                  );
                  await checkP2AProviderCapabilities(recipientAccountDetails.routing_number);
                }
              }}
            >
              Confirm
            </button>
            {renderP2AGoBack()}
          </>
        );
      case "visaAliasEnter" === viewState:
        return (
          <>
            {TransferDetails()}
            <h5>
              Enter the phone number, email, or $Cashtag connected to your{" "}
              {visaAliasMeta(visaAliasDirectory).name} account.
            </h5>
            <label htmlFor="recipient_identifier">
              Phone number, email, or $Cashtag
            </label>
            <input
              id="recipient_identifier"
              aria-required="true"
              value={recipientIdentifier}
              onChange={(e) => handleRecipientIdentifierChange(e)}
              placeholder="Phone number, email, or $Cashtag"
              disabled={loading}
            />
            {errorIdentifier && (
              <div className="np-error">
                Please enter a valid phone number, email address, or $Cashtag.
              </div>
            )}
            <label htmlFor="visa_alias_first_name">First name</label>
            <input
              id="visa_alias_first_name"
              aria-required="true"
              value={visaAliasDetails.firstName}
              onChange={(e) => handleVisaAliasDetailsChange(e)}
              placeholder="First name"
              disabled={loading}
              style={
                errorVisaAliasFirstName
                  ? { borderColor: "red", borderWidth: "2px" }
                  : {}
              }
            />
            {errorVisaAliasFirstName && (
              <div className="np-error">
                Please enter your first name.
              </div>
            )}
            <label htmlFor="visa_alias_last_name">Last name</label>
            <input
              id="visa_alias_last_name"
              aria-required="true"
              value={visaAliasDetails.lastName}
              onChange={(e) => handleVisaAliasDetailsChange(e)}
              placeholder="Last name"
              disabled={loading}
              style={
                errorVisaAliasLastName
                  ? { borderColor: "red", borderWidth: "2px" }
                  : {}
              }
            />
            {errorVisaAliasLastName && (
              <div className="np-error">
                Please enter your last name.
              </div>
            )}
            {/* Only shown when the alias isn't already a phone number. Visa needs the last 4 of
                the wallet's contact number; when the alias IS a phone we take it from there and
                don't ask twice. */}
            {visaAliasNeedsPhone() && (
              <>
                <label htmlFor="visa_alias_phone">
                  Phone number on your{" "}
                  {visaAliasMeta(visaAliasDirectory).name} account
                  
                </label>
                <input
                  id="visa_alias_phone"
                  aria-required="true"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={visaAliasDetails.phone}
                  onChange={(e) => handleVisaAliasDetailsChange(e)}
                  placeholder="Phone number"
                  disabled={loading}
                  style={
                    errorVisaAliasPhone
                      ? { borderColor: "red", borderWidth: "2px" }
                      : {}
                  }
                />
                {errorVisaAliasPhone && (
                  <div className="np-error">
                    Please enter a valid phone number.
                  </div>
                )}
              </>
            )}
            {visaAliasError && (
              <div className="np-error">
                {visaAliasError}
              </div>
            )}
            {/* Primary styling per Visa's Aug 2026 UX review: filled with the FI's branded
                accent so the main action is distinguishable from the input fields above it and
                from the secondary "Go Back" below. */}
            <button
              onClick={resolveVisaAlias}
              disabled={loading}
              className="button_primary"
            >
              {loading ? (
                <span className="button_spinner" aria-label="Loading" />
              ) : (
                "Next"
              )}
            </button>
            <button
              onClick={() => {
                setRecipientType("");
                setRecipientIdentifier(
                  transferData.recipient_identifier || transferData.payeeEmail
                );
                returnHome();
              }}
            >
              Go Back
            </button>
          </>
        );
      case "visaAliasWalletAction" === viewState:
        return (
          <>
            {TransferDetails()}
            {/* This press cost an attempt like any other, so the count is shown here too —
                sending someone off to their wallet app without mentioning it is how they come
                back to a payment that has quietly locked. At zero the instruction itself is
                dropped: coming back to try again is no longer possible, and telling them to
                retry and to stop retrying in one breath is worse than saying neither. */}
            {visaAliasAttemptsLeft === 0 ? (
              <h5>
                You're out of attempts for this payment. Please choose a
                different way to get paid.
              </h5>
            ) : (
              <h5>
                Visit your {visaAliasMeta(visaAliasDirectory).name} app to enable
                this transaction, then come back and try again.
                {visaAliasAttemptsLeft > 1
                  ? ` You have ${visaAliasAttemptsLeft} attempts left.`
                  : visaAliasAttemptsLeft === 1
                  ? " This is your last attempt."
                  : ""}
              </h5>
            )}
            <button
              className="button_primary"
              onClick={() => {
                setVisaAliasError("");
                setViewState("visaAliasEnter");
              }}
              disabled={loading}
            >
              Try Again
            </button>
            <button
              onClick={() => {
                setRecipientType("");
                setRecipientIdentifier(
                  transferData.recipient_identifier || transferData.payeeEmail
                );
                returnHome();
              }}
            >
              Choose Another Way
            </button>
          </>
        );
      case "visaAliasConfirm" === viewState:
        return (
          <>
            {TransferDetails()}
            {/* Neither the wallet's name-on-file nor the alias itself is shown: the recipient
                already proved they know the name, and printing either would let anyone with a
                phone number learn something about that wallet account. */}
            <h5>
              We have successfully verified your{" "}
              {visaAliasMeta(visaAliasDirectory).name} account.
            </h5>
            {/* Mirrors the homepage button exactly — wallet icon, label, Visa lockup — so the
                same action doesn't look like two different things across screens. Visa's Aug
                2026 UX review asked for the Cash App treatment to be consistent on both. The
                class and icon markup match `renderButton` so the flex layout behaves the same. */}
            <button
              onClick={submitTransfer}
              disabled={loading}
              className={
                !loading && visaAliasMeta(visaAliasDirectory).icon
                  ? "button_with_icon button_primary"
                  : "button_primary"
              }
            >
              {loading ? (
                <span className="button_spinner" aria-label="Loading" />
              ) : (
                // Identical structure to the homepage button, which `renderButton` builds as
                // THREE flex children: icon, label, and a 32px spacer. Under space-between the
                // spacer is what keeps the label centred — without it the label is shoved flush
                // right and the absolutely-positioned Visa lockup lands off the screen edge.
                <>
                  {visaAliasMeta(visaAliasDirectory).icon && (
                    <span style={{ width: "32px", height: "32px" }}>
                      {visaAliasMeta(visaAliasDirectory).icon}
                    </span>
                  )}
                  {renderVisaAliasButtonLabel(visaAliasDirectory)}
                  {visaAliasMeta(visaAliasDirectory).icon && (
                    <span
                      style={{ width: "32px", height: "32px" }}
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
            </button>
            <button
              onClick={() => {
                setVisaAliasError("");
                setViewState("visaAliasEnter");
              }}
            >
              Go Back
            </button>
          </>
        );
      default:
        return renderHomepage();
    }
  };

  // Claim screen: amount card, section heading, payout chooser. Also rendered
  // underneath the decline dialog. The "via your choice of the service below"
  // line stays an h5 — the Cypress suite reads it as the standard-send marker.
  const renderHomepage = () => (
    <>
      {TransferDetails()}
      <h2 className="np-section">How would you like to get paid?</h2>
      <h5 className="np-h-sub np-caption">
        Payment will be sent to {getRecipientIdentifierDisplay()} via your
        choice of the service below.
      </h5>
      {coinbaseError && <div className="np-error">{coinbaseError}</div>}
      {renderButtons("homepage")}
    </>
  );

  const hasLogo =
    typeof branding.receiving_logo_url === "string" &&
    branding.receiving_logo_url.length > 0 &&
    parseFloat(branding.receiving_logo?.logo_height) > 0;

  return (
    <main>
      <nav aria-label="Header">
        {hasLogo && (
          <div className="np-brand">
            <div className="logo"></div>
          </div>
        )}
      </nav>
      <div className="np-screen">{renderScreen()}</div>
    </main>
  );
};

export default ExternalTransfer;
